/**
 * PanelSidebar — shared sidebar renderer for Admin, Instructor, and Student layouts.
 * Mobile: sidebar hidden, hamburger opens overlay drawer.
 * Desktop: fixed sidebar with collapse toggle.
 */
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, PanelLeft, ArrowLeft, Menu, X } from 'lucide-react'
import { useAuth } from '@/stores/auth-context'
import { Outlet } from 'react-router-dom'
import { useState, useEffect } from 'react'
import type { LucideIcon } from 'lucide-react'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SidebarItem {
  label: string
  href: string
  icon: LucideIcon
}

export interface SidebarSection {
  label: string
  items: SidebarItem[]
}

interface PanelLayoutProps {
  sections: SidebarSection[]
  panelLabel: string
  defaultRole?: string
}

// ─── Sidebar Content (shared between desktop and mobile) ────────────────────

function SidebarContent({
  sections,
  collapsed,
  onLinkClick,
}: {
  sections: SidebarSection[]
  collapsed: boolean
  onLinkClick?: () => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const displayName = user?.full_name || 'User'
  const displayEmail = user?.email || ''
  const initial = displayName.charAt(0).toUpperCase()

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <>
      {/* Avatar + name */}
      <div className={`pt-5 pb-3 ${collapsed ? 'px-3' : 'px-4'}`}>
        <div className={`flex ${collapsed ? 'justify-center' : 'flex-col'}`}>
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <div className="mt-2 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-[13px] font-semibold text-foreground truncate">{displayName}</p>
                {user?.role && user.role !== 'student' && (
                  <span className="text-[8px] font-mono tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 flex-shrink-0">
                    {user.role}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{displayEmail}</p>
            </div>
          )}
        </div>
      </div>

      {/* Nav sections */}
      <nav className={`flex-1 overflow-y-auto ${collapsed ? 'px-1.5' : 'px-2'}`}>
        {sections.map((section, sIdx) => (
          <div key={section.label}>
            {!collapsed ? (
              <div className="px-2 pt-4 pb-2">
                <span className="text-[9px] font-mono text-muted-foreground tracking-[0.2em] uppercase">
                  {section.label}
                </span>
              </div>
            ) : (
              sIdx > 0 && <div className="mx-2 my-2 border-t border-white/[0.06]" />
            )}

            <div className="space-y-0.5">
              {section.items.map((link) => {
                const active =
                  location.pathname === link.href ||
                  (link.href !== '/' && location.pathname.startsWith(link.href + '/'))
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={onLinkClick}
                    title={collapsed ? link.label : undefined}
                    className={`flex items-center gap-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                      collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'
                    } ${
                      active
                        ? 'text-primary bg-primary/[0.08]'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
                    }`}
                  >
                    <link.icon className="flex-shrink-0" style={{ width: 17, height: 17 }} />
                    {!collapsed && link.label}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className={`pb-4 space-y-0.5 ${collapsed ? 'px-1.5' : 'px-2'}`}>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-2.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors w-full ${
            collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'
          }`}
        >
          <LogOut className="flex-shrink-0" style={{ width: 17, height: 17 }} />
          {!collapsed && 'Log Out'}
        </button>
      </div>
    </>
  )
}

// ─── Top bar ────────────────────────────────────────────────────────────────

function TopBar({
  panelLabel,
  collapsed,
  onToggle,
  onMobileMenuToggle,
  mobileOpen,
}: {
  panelLabel: string
  collapsed: boolean
  onToggle: () => void
  onMobileMenuToggle: () => void
  mobileOpen: boolean
}) {
  return (
    <header className="sticky top-0 z-30 h-12 flex items-center justify-between px-4 sm:px-6 border-b border-white/[0.06] bg-background/80 backdrop-blur-xl">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X style={{ width: 17, height: 17 }} /> : <Menu style={{ width: 17, height: 17 }} />}
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={onToggle}
          className="hidden lg:block p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <PanelLeft style={{ width: 17, height: 17 }} />
        </button>

        <div className="hidden sm:flex items-center gap-2">
          <div className="h-px w-6 bg-primary/30" />
          <span className="text-[10px] font-mono text-primary/60 tracking-[0.2em] uppercase">
            {panelLabel}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Back to home */}
        <Link
          to="/"
          className="hidden sm:flex text-[12px] text-muted-foreground hover:text-foreground transition-colors items-center gap-1 ml-1"
        >
          <ArrowLeft style={{ width: 14, height: 14 }} />
          Back to Home
        </Link>
      </div>
    </header>
  )
}

// ─── PanelLayout (exported) ─────────────────────────────────────────────────

export default function PanelLayout({ sections, panelLabel }: PanelLayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Close mobile menu on route change
  const location = useLocation()
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar — hidden on mobile */}
      <aside
        className={`hidden lg:flex fixed top-0 left-0 h-full z-40 flex-col border-r border-white/[0.06] bg-background transition-all duration-300 ${
          collapsed ? 'w-[60px]' : 'w-[220px]'
        }`}
      >
        <SidebarContent sections={sections} collapsed={collapsed} />
      </aside>

      {/* Mobile overlay + drawer — always rendered, toggled via translate */}
      <div
        className={`lg:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      />
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full z-[70] flex flex-col w-[280px] bg-background border-r border-white/[0.12] shadow-2xl transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <span className="text-[11px] font-mono text-primary/60 tracking-[0.2em] uppercase">{panelLabel}</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-white/[0.06]"
          >
            <X style={{ width: 17, height: 17 }} />
          </button>
        </div>
        <SidebarContent sections={sections} collapsed={false} onLinkClick={() => setMobileOpen(false)} />
      </aside>

      {/* Main content */}
      <div
        className={`flex-1 min-w-0 transition-all duration-300 ${
          collapsed ? 'lg:ml-[60px]' : 'lg:ml-[220px]'
        }`}
      >
        <TopBar
          panelLabel={panelLabel}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          onMobileMenuToggle={() => setMobileOpen((o) => !o)}
          mobileOpen={mobileOpen}
        />
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
